import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, withRouter } from 'react-router';
import Cookies from 'universal-cookie';
import URL from 'url-parse';

import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import StarsModal from '@swf/components/StarsModal/StarsModal';
import StarsSection from '@swf/components/StarsSection/StarsSection';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import CookieNames from '@swf/core/types/Cookies';
import type { StaticContext } from '@swf/core/types/StaticContext';
import { absoluteURL, canUseDOM, decodeQuery } from '@swf/core/utils';

import AlternateLinks from './components/AlternateLinks/AlternateLinks';
import CanonicalLink from './components/CanonicalLink/CanonicalLink';
import MetaDescription from './components/MetaDescription/MetaDescription';
import MetaNoIndex from './components/MetaNoIndex/MetaNoIndex';
import MetaTags from './components/MetaTags/MetaTags';
import MetaTitle from './components/MetaTitle/MetaTitle';
import NotFound from './components/NotFound/NotFound';

interface Props {
  staticContext?: StaticContext;
}

const cookies = new Cookies();

const StarsPage: React.FC<Props> = ({ staticContext = {} }) => {
  const { layout, meta, url } = useSelector(select.page.get);
  const pageUrl = useSelector(select.page.getUrl);
  const redirect = useSelector(select.location.getRedirectPath);
  const query = useSelector(select.location.getQuery);
  const dispatch = useDispatch<Dispatch>();
  const loading = useSelector((state: RootState) => state.loading.models.page);

  const pathname = useSelector((rootState: RootState) =>
    select.location.stripLocale(rootState, undefined)
  );

  staticContext.prefetch = async ({ dispatch }, version) => {
    await dispatch.page.fetch({ href: pathname, version });
  };

  useEffect(() => {
    if (!loading && (!layout || url?.pathname !== pathname)) {
      dispatch.page.fetch({ href: window.location.href });
    }
  }, [dispatch.page, pathname, loading, pageUrl, layout, url?.pathname]);

  useEffect(() => {
    if (redirect) dispatch.location.update(new URL(window.location.href, true));
  }, [dispatch.location, redirect]);

  useEffect(() => {
    const { modal, mitid, ...rest } = query;
    if (modal) dispatch.modal.enqueue({ path: modal, locationState: decodeQuery(mitid) || rest });
  }, [dispatch.modal, query]);

  useEffect(() => {
    if (
      meta?.redirect &&
      meta.redirect.statusCode &&
      [200, 404].includes(meta.redirect.statusCode) &&
      !cookies.get(CookieNames.SOFT_REDIRECT_CANCELLED)
    ) {
      dispatch.modal.enqueue({ path: '/notifications/redirected' });
    }
  }, [dispatch.modal, meta?.redirect]);

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  staticContext.statusCode = layout ? 200 : 404;

  if (meta?.redirect && meta.redirect.statusCode && [301, 302].includes(meta.redirect.statusCode)) {
    staticContext.statusCode = meta.redirect.statusCode;
    const { location } = meta.redirect;

    if (absoluteURL(location)) {
      canUseDOM ? (window.location.href = location) : (staticContext.url = location);
      return null;
    }

    return <Redirect to={location} />;
  }

  return (
    <>
      <MetaTitle />
      <MetaDescription />
      <MetaNoIndex />
      <AlternateLinks />
      <CanonicalLink />
      <MetaTags />
      {layout ? (
        <>
          <StarsSection layout={layout} />
          <StarsModal />
        </>
      ) : (
        <>
          <CacheControl>no-store</CacheControl>
          <NotFound />
        </>
      )}
    </>
  );
};

export default withRouter(StarsPage);
